/**
 * The signed-in account page — `/profile` (routes/public.jsx, inside RequireAuth, under
 * PublicLayout in both SLOT_MODEL states). Every signed-in user reaches it from the account
 * menu ("My profile"): guests who set a password, business owners, venue partners.
 *
 * What it does (unchanged by the redesign):
 * - shows the account: photo, name, email (+ "Verified"), phone, birthday, city/state;
 * - edits first name, last name, phone (formatted "(555) 555-5555" while typing) and birthday
 *   through AuthContext.updateProfile → PUT /api/auth/profile { first_name, last_name, phone,
 *   birthday }, which refreshes the user;
 * - uploads a photo: POST /api/auth/upload-avatar (multipart `image`, fetch with the auth
 *   headers so the browser sets the boundary; image/* only, 5 MB), then refreshUser();
 * - a password account with an unverified email can resend the verification email
 *   (POST /api/auth/resend-verification);
 * - Log out (AuthContext.logout, which also clears the kept wallet token);
 * - signed out after loading → back to "/".
 *
 * Look (site redesign Phase 6, components/site on the brand tokens; replaces the V5 palette,
 * inline styles and animated blobs): the page's own <main id="main"> clearing the fixed
 * header, PageHero (the one h1), then one 1050px column: a status line for save / upload
 * results, the two notices (verify email: butter card; complete your profile: lilac card; navy
 * text on both, >= 11.4:1), the identity card (square photo or initial on peach, name, email)
 * and the "Personal details" card (a definition list, or the form with square sheet fields),
 * and Log out.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { Button, Heading, PageHero, Pill, SiteCard, SiteContainer } from '../components/site';
import { useAuth } from '../context/AuthContext';
import { resolveAssetUrl } from '../config';
import { api, authHeaders } from '../lib/api';
import { cn } from '../lib/utils';
import { PageLoading, SITE_HEADER_OFFSET } from '../layouts/PageStates';

export const MAX_AVATAR_BYTES = 5 * 1024 * 1024;

/**
 * Cards on a narrow screen with enlarged text (under 20em: 320px at 200%) keep their padding
 * to 1rem, so headings and button labels keep enough room not to break mid-word.
 */
const TIGHT_CARD = '[@media(max-width:19.9375em)]:p-4';

/** "(555) 555-5555" from any input: digits only, at most 10, formatted as typed. */
export function formatPhoneNumber(value) {
  const limited = String(value || '').replace(/\D/g, '').slice(0, 10);
  if (limited.length === 0) return '';
  if (limited.length <= 3) return `(${limited}`;
  if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
}

/** A stored phone for display: kept as it is when already formatted, else formatted. */
export function displayPhone(phone) {
  if (!phone) return '';
  return String(phone).includes('(') ? String(phone) : formatPhoneNumber(phone);
}

/**
 * "May 15, 1990" from the stored "YYYY-MM-DD". Read as a calendar date (local), never as UTC
 * midnight, which would show the day before west of Greenwich. Other strings go through Date.
 */
export function formatBirthday(value) {
  if (!value) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

const fieldsFrom = (user) => ({
  first_name: user?.first_name || '',
  last_name: user?.last_name || '',
  phone: user?.phone || '',
  birthday: user?.birthday || '',
});

// The brand's form parts (enquiry / free-listing forms): a visible 14px label, a square 16px
// sheet field with a navy/60 border (4.34:1 on sheet).
const FIELD =
  'block min-h-[47px] w-full min-w-0 rounded-none border border-navy/60 bg-brand-sheet px-3 py-2.5 font-body text-base text-navy placeholder:text-brand-muted disabled:cursor-progress disabled:opacity-60';
const LABEL = 'mb-1.5 block text-sm font-medium leading-snug';
const TERM = 'font-label text-[0.6875rem] uppercase leading-normal tracking-[0.07em] text-brand-muted';

function Detail({ term, value }) {
  return (
    <div className="min-w-0">
      <dt className={TERM}>{term}</dt>
      <dd className={cn('m-0 mt-1 break-words text-base', value ? 'text-navy' : 'text-brand-muted')}>{value || 'Not set'}</dd>
    </div>
  );
}

/** Save / upload results: one polite live region, present from the first render. */
function StatusLine({ message }) {
  return (
    <div role="status" aria-live="polite" data-testid="profile-status">
      {message.text ? (
        <p
          className={cn(
            'm-0 border-l-4 px-4 py-3 text-[0.9375rem] font-medium leading-relaxed',
            message.type === 'success' ? 'border-navy bg-brand-sage text-navy' : 'border-brand-rust bg-brand-sheet text-brand-rust',
          )}
        >
          {message.text}
        </p>
      ) : null}
    </div>
  );
}

export default function ProfileNew() {
  const { user, isAuthenticated, loading, logout, updateProfile, refreshUser } = useAuth();
  const navigate = useNavigate();
  const uid = useId();
  const fileInputRef = useRef(null);
  const firstFieldRef = useRef(null);
  const editButtonRef = useRef(null);
  const focusEditButton = useRef(false);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [resending, setResending] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState({ type: '', text: '' });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState(() => fieldsFrom(null));

  useEffect(() => {
    if (!loading && !isAuthenticated) navigate('/');
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (user) setFormData(fieldsFrom(user));
  }, [user]);

  // Edit → the first field; Save or Cancel → back to the Edit button.
  useEffect(() => {
    if (editing) firstFieldRef.current?.focus();
    else if (focusEditButton.current) {
      focusEditButton.current = false;
      editButtonRef.current?.focus();
    }
  }, [editing]);

  if (loading) return <PageLoading />;
  if (!user) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: name === 'phone' ? formatPhoneNumber(value) : value }));
  };

  const startEditing = () => {
    setMessage({ type: '', text: '' });
    setEditing(true);
  };

  const handleCancel = () => {
    setFormData(fieldsFrom(user));
    setMessage({ type: '', text: '' });
    focusEditButton.current = true;
    setEditing(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setMessage({ type: '', text: '' });
    const result = await updateProfile(formData);
    if (result?.success) {
      setMessage({ type: 'success', text: 'Your details are saved.' });
      focusEditButton.current = true;
      setEditing(false);
    } else {
      setMessage({ type: 'error', text: result?.error || "We couldn't save your details. Please try again." });
    }
    setSaving(false);
  };

  const handleAvatarUpload = async (e) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;
    // The same file can be picked again after an error.
    const reset = () => {
      try {
        input.value = '';
      } catch {
        /* read-only in some browsers */
      }
    };
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Choose an image file for your photo.' });
      reset();
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      setMessage({ type: 'error', text: 'Choose an image under 5 MB.' });
      reset();
      return;
    }

    setUploading(true);
    setMessage({ type: '', text: '' });
    try {
      const body = new FormData();
      body.append('image', file);
      // Multipart, so it stays on fetch — the browser must set the boundary.
      const response = await fetch(`${api.defaults.baseURL}/auth/upload-avatar`, {
        method: 'POST',
        headers: authHeaders(),
        body,
      });
      if (response.ok) {
        setMessage({ type: 'success', text: 'Your photo is updated.' });
        await refreshUser();
      } else {
        let uploadError = "We couldn't upload your photo. Please try again.";
        try {
          const errorData = await response.json();
          if (typeof errorData?.detail === 'string' && errorData.detail) uploadError = errorData.detail;
        } catch {
          // No JSON body: keep the default message.
        }
        setMessage({ type: 'error', text: uploadError });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error?.name === 'TypeError'
          ? 'Network error. Please check your connection and try again.'
          : "We couldn't upload your photo. Please try again.",
      });
    } finally {
      setUploading(false);
      reset();
    }
  };

  const handleResendVerification = async () => {
    setResending(true);
    setVerifyMessage({ type: '', text: '' });
    try {
      await api.post('/auth/resend-verification', {}, { withCredentials: true });
      setVerifyMessage({ type: 'success', text: 'Verification email sent. Check your inbox.' });
    } catch {
      setVerifyMessage({ type: 'error', text: 'Something went wrong. Please try again.' });
    } finally {
      setResending(false);
    }
  };

  const needsEmailVerification = !user.email_verified && user.oauth_provider === 'password';
  const isProfileIncomplete = !user.first_name || !user.last_name || !user.birthday;
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
  const initial = (user.first_name?.[0] || user.email?.[0] || '?').toUpperCase();
  const location = [user.location_city, user.location_state].filter(Boolean).join(', ');
  const ids = {
    first: `${uid}-first-name`,
    last: `${uid}-last-name`,
    phone: `${uid}-phone`,
    birthday: `${uid}-birthday`,
    details: `${uid}-details`,
  };

  return (
    <main id="main" className={cn(SITE_HEADER_OFFSET, 'site-light pb-section text-navy')} data-testid="profile-page">
      <SEO title="Your profile" description="Your Tappd account details." url="/profile" noIndex />
      {/* One column, 1050px: the hero and the cards share the same left edge. */}
      <div className="mx-auto max-w-[65.625rem]">
        <PageHero
          eyebrow="Your account"
          title={<>Your <em>profile</em></>}
          accent="rust"
          lede="Update your name, phone and birthday, or change your photo."
          actions={(
            <Button to="/deal-wallet" variant="secondary">
              Your offer codes
            </Button>
          )}
        />

        <SiteContainer width="full" className="space-y-6">
          <StatusLine message={message} />

          {needsEmailVerification && (
            <SiteCard as="section" aria-labelledby={`${uid}-verify`} tint="butter" accent="rust" className={TIGHT_CARD} data-testid="profile-verify">
              <Heading level={2} size="minor" id={`${uid}-verify`} className="m-0">
                Verify your email address
              </Heading>
              <p className="mb-0 mt-2 max-w-[40rem] text-[0.9375rem] leading-relaxed">
                We sent a verification link to <span className="break-words font-semibold">{user.email}</span>. Can&apos;t find
                it? We can send it again.
              </p>
              <Button
                variant="secondary"
                size="sm"
                className="mt-4"
                onClick={handleResendVerification}
                loading={resending}
                loadingLabel="Sending…"
              >
                Resend verification email
              </Button>
              <div role="status" aria-live="polite" className="mt-3 text-[0.9375rem]">
                {verifyMessage.text ? (
                  <p className={cn('m-0 font-medium', verifyMessage.type === 'error' ? 'text-brand-rust' : 'text-navy')}>
                    {verifyMessage.text}
                  </p>
                ) : null}
              </div>
            </SiteCard>
          )}

          {isProfileIncomplete && !editing && (
            <SiteCard as="section" aria-labelledby={`${uid}-incomplete`} tint="lilac" className={TIGHT_CARD} data-testid="profile-incomplete">
              <Heading level={2} size="minor" id={`${uid}-incomplete`} className="m-0">
                Complete your profile
              </Heading>
              <p className="mb-0 mt-2 max-w-[40rem] text-[0.9375rem] leading-relaxed">Add your name and birthday to your account.</p>
              <Button size="sm" className="mt-4" onClick={startEditing}>
                Add your details
              </Button>
            </SiteCard>
          )}

          <div className="grid gap-6 nav:grid-cols-[minmax(0,19rem)_minmax(0,1fr)] nav:items-start">
            <SiteCard as="section" aria-labelledby={`${uid}-name`} className={cn('min-w-0', TIGHT_CARD)} data-testid="profile-identity">
              {/* Photo and name side by side while the name column has room (11rem, so it follows
                  the text size), else the name wraps under the photo; stacked from 800px. */}
              <div className="flex flex-wrap items-center gap-5 nav:flex-col nav:flex-nowrap nav:items-start">
                {user.picture ? (
                  <img
                    src={resolveAssetUrl(user.picture)}
                    alt="Your profile photo"
                    className="aspect-square w-24 max-w-full shrink-0 border border-navy bg-brand-sheet object-cover shadow-site-card nav:w-28"
                  />
                ) : (
                  <span
                    aria-hidden="true"
                    className="grid aspect-square w-24 max-w-full shrink-0 place-items-center overflow-hidden border border-navy bg-brand-peach font-display text-[2.75rem] font-medium leading-none text-navy shadow-site-card nav:w-28"
                    data-testid="profile-initial"
                  >
                    {initial}
                  </span>
                )}
                <div className="min-w-[min(100%,11rem)] flex-1 nav:w-full nav:flex-none" data-testid="profile-identity-text">
                  <Heading level={2} size="card" id={`${uid}-name`} className="m-0">
                    {fullName || 'Add your name'}
                  </Heading>
                  <p className="mb-0 mt-2 break-words text-[0.9375rem] leading-snug text-brand-muted">{user.email}</p>
                  {user.email_verified ? (
                    <Pill tone="sage" className="mt-2">Verified</Pill>
                  ) : null}
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                arrow={false}
                className="mt-5"
                onClick={() => fileInputRef.current?.click()}
                loading={uploading}
                loadingLabel="Uploading…"
              >
                {user.picture ? 'Change photo' : 'Add a photo'}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarUpload}
                className="hidden"
                tabIndex={-1}
                aria-hidden="true"
                data-testid="profile-photo-input"
              />
              <p className="mb-0 mt-2 text-[0.8125rem] leading-snug text-brand-muted">An image file up to 5 MB.</p>
            </SiteCard>

            <SiteCard as="section" aria-labelledby={ids.details} className={cn('min-w-0', TIGHT_CARD)} data-testid="profile-details">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Heading level={2} size="card" id={ids.details} className="m-0">
                  Personal details
                </Heading>
                {!editing ? (
                  <Button ref={editButtonRef} variant="secondary" size="sm" arrow={false} onClick={startEditing}>
                    Edit details
                  </Button>
                ) : null}
              </div>

              {!editing ? (
                <dl className="m-0 mt-6 grid gap-x-8 gap-y-5 sm:grid-cols-2">
                  <Detail term="First name" value={user.first_name} />
                  <Detail term="Last name" value={user.last_name} />
                  <Detail term="Email address" value={user.email} />
                  <Detail term="Phone" value={displayPhone(user.phone)} />
                  <Detail term="Birthday" value={formatBirthday(user.birthday)} />
                  {location ? <Detail term="Location" value={location} /> : null}
                </dl>
              ) : (
                <form noValidate onSubmit={handleSave} aria-busy={saving || undefined} className="mt-6">
                  <div className="grid gap-x-6 gap-y-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor={ids.first} className={LABEL}>First name</label>
                      <input
                        ref={firstFieldRef}
                        id={ids.first}
                        name="first_name"
                        type="text"
                        autoComplete="given-name"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        className={FIELD}
                      />
                    </div>
                    <div>
                      <label htmlFor={ids.last} className={LABEL}>Last name</label>
                      <input
                        id={ids.last}
                        name="last_name"
                        type="text"
                        autoComplete="family-name"
                        value={formData.last_name}
                        onChange={handleInputChange}
                        className={FIELD}
                      />
                    </div>
                    <div>
                      <label htmlFor={ids.phone} className={LABEL}>Phone</label>
                      <input
                        id={ids.phone}
                        name="phone"
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel-national"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="(555) 123-4567"
                        className={FIELD}
                      />
                    </div>
                    <div>
                      <label htmlFor={ids.birthday} className={LABEL}>Birthday</label>
                      <input
                        id={ids.birthday}
                        name="birthday"
                        type="date"
                        autoComplete="bday"
                        value={formData.birthday}
                        onChange={handleInputChange}
                        className={FIELD}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <p className={cn(TERM, 'm-0')}>Email address</p>
                      <p className="m-0 mt-1 break-words text-base">{user.email}</p>
                    </div>
                  </div>
                  <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                    <Button type="submit" loading={saving} loadingLabel="Saving…">
                      Save details
                    </Button>
                    <Button variant="secondary" arrow={false} onClick={handleCancel} disabled={saving}>
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </SiteCard>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4 border-t border-brand-rule pt-6">
            {/* min-w-0: a long address wraps inside the row instead of widening the page. */}
            <p className="m-0 min-w-0 max-w-full break-words text-[0.9375rem] text-brand-muted" data-testid="profile-signed-in">
              Signed in as <span className="font-semibold text-navy">{user.email}</span>
            </p>
            <Button variant="secondary" arrow={false} onClick={logout}>
              Log out
            </Button>
          </div>
        </SiteContainer>
      </div>
    </main>
  );
}
